import { useCallback, useEffect, useState } from "react";
import { createClass, deleteClass, listClasses, mergeClass, updateClass } from "../api/projects";
import type { LabelClass } from "../api/types";
import { nextColor } from "../lib/classColors";
import { Dialog } from "../ui/Dialog";
import { useDialogs } from "../ui/dialogs";
import { ClassColorDialog } from "./ClassColorDialog";

interface ColorAsk { title: string; name: string; initial: string; others: LabelClass[]; resolve: (hex: string | null) => void }

/** class_manager_dialog.py */
export function ClassManagerDialog({ projectId, onClose }: { projectId: number; onClose: () => void }) {
  const dlg = useDialogs();
  const [classes, setClasses] = useState<LabelClass[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const current = classes.find((c) => c.id === selected) ?? null;
  // The colour picker, open while a colour is being chosen (QColorDialog.getColor, as a promise).
  const [asking, setAsking] = useState<ColorAsk | null>(null);
  const pickColor = (ask: Omit<ColorAsk, "resolve">) => new Promise<string | null>((resolve) => setAsking({ ...ask, resolve }));

  const load = useCallback(async () => {
    try { setClasses(await listClasses(projectId)); }
    catch (e) { await dlg.alert({ title: "Could not load the classes", text: (e as Error).message }); }
  }, [projectId, dlg]);
  useEffect(() => { void load(); }, [load]);

  const attempt = async (fn: () => Promise<unknown>) => {
    // Like the desktop, reloading the list drops the selection.
    try { await fn(); setSelected(null); await load(); } catch (e) { await dlg.alert({ title: "Could not change the classes", text: (e as Error).message }); }
  };

  const add = async () => {
    const name = (await dlg.prompt({ title: "New class", label: "Class name" }))?.trim();
    if (!name) return;
    // A colour no class has yet, to start with. Cancel here adds nothing (the desktop adds the class in its default colour).
    const color = await pickColor({ title: `Colour for '${name}'`, name, initial: nextColor(classes.map((c) => c.color_hex)), others: classes });
    if (color) await attempt(() => createClass(projectId, name, color));
  };
  const rename = async () => {
    if (!current) return;
    const name = (await dlg.prompt({ title: "Rename class", label: "Class name", initial: current.name }))?.trim();
    if (name) await attempt(() => updateClass(projectId, current.id, { name }));
  };
  const recolor = async () => {
    if (!current) return;
    const color = await pickColor({ title: `Colour for '${current.name}'`, name: current.name, initial: current.color_hex,
      others: classes.filter((c) => c.id !== current.id) });
    if (color) await attempt(() => updateClass(projectId, current.id, { color_hex: color }));
  };
  const merge = async () => {
    if (!current) return;
    const others = classes.filter((c) => c.id !== current.id);
    if (!others.length) { await dlg.alert({ title: "Merge classes", text: "There are no other classes to merge into." }); return; }
    const targetName = await dlg.select({ title: "Merge classes", label: `Merge '${current.name}' into`, options: others.map((o) => o.name) });
    if (!targetName) return;
    const target = others.find((o) => o.name === targetName)!;
    const yes = await dlg.confirm({
      title: "Merge classes", yes: "Merge", no: "Cancel",
      text: `Merge '${current.name}' into '${target.name}'?\n\nEvery annotation labelled '${current.name}' will be relabelled '${target.name}', and '${current.name}' will be removed as a separate class. Models that still predict '${current.name}' will keep landing on '${target.name}'.`,
    });
    if (yes) { setSelected(null); await attempt(() => mergeClass(projectId, target.id, current.id)); }
  };
  const remove = async () => {
    if (!current) return;
    if (await dlg.confirm({ title: "Delete class", danger: true, yes: "Delete", no: "Cancel", text: `Delete the class '${current.name}'?` })) { setSelected(null); await attempt(() => deleteClass(projectId, current.id)); }
  };

  return (
    <Dialog title="Manage Classes" onClose={onClose} width={520} testId="class-manager">
      <div className="muted" style={{ marginBottom: 6 }}>Label Classes</div>
      <ul className="list" style={{ height: 300 }} role="listbox" data-testid="class-list">
        {classes.map((c) => (
          <li key={c.id} role="option" aria-selected={c.id === selected} style={{ color: c.id === selected ? undefined : c.color_hex }}
            onClick={() => setSelected(c.id)} data-testid={`class-${c.name}`}>{c.name}</li>
        ))}
      </ul>
      <div className="dialog-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
        <button className="btn primary" onClick={add}>+ Add Class</button>
        <button className="btn" onClick={rename}>Rename</button>
        <button className="btn" onClick={recolor}>Change Color</button>
        <button className="btn" onClick={merge}>Merge Into…</button>
        <button className="btn danger" onClick={remove}>Delete</button>
      </div>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Close</button></div>
      {asking && (
        <ClassColorDialog title={asking.title} name={asking.name} initial={asking.initial} others={asking.others}
          onDone={(hex) => { setAsking(null); asking.resolve(hex); }} />
      )}
    </Dialog>
  );
}
